import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import styles from "./OrderConfirmation.module.css";
import Api from "../../utils/Api/Api";
import { track, EVENTS } from "../../utils/analyticsService";

const SUPPORT_NUMBER = "919148477518";

/* Cadence. Verification is a race against the gateway's own webhook, so the
   first look is quick, the next few are patient, and a run that drags gets
   slower rather than louder. */
const FIRST_POLL_MS = 1000;
const POLL_MS = 2000;
const PATIENT_POLL_MS = 5000;
const SLOW_AFTER = 3;
const GIVE_UP_AFTER = 6;
const ERROR_BACKOFF_CAP_MS = 8000;
const REDIRECT_HOLD_MS = 1000;
const MISSING_ORDER_REDIRECT_MS = 2000;

const ERROR_COPY = {
  missing_order_id: "Unable to locate your order information",
  payment_not_successful: "Your payment was not successful",
  no_order_found: "Order details not found",
  gateway_error: "There was an issue with the payment gateway",
  timeout: "Payment verification timed out",
  unknown_error: "An unexpected error occurred",
};

const messageFor = (code, gateway) => {
  if (code === "gateway_error" && gateway) {
    return `There was an issue with the ${gateway} gateway`;
  }
  return ERROR_COPY[code] || "Payment was unsuccessful";
};

const VERIFYING = {
  phase: "verifying",
  title: "We're confirming your payment...",
  body: "You'll be redirected automatically in a few seconds",
};

/**
 * Order confirmation, rebuilt from design/Purchase Flow.dc.html.
 *
 * Written from scratch, presentation and logic both. Where the gateway sends
 * the customer back before its webhook has landed, this screen polls until the
 * order is definitively settled - and treats "not yet" and "no" as different
 * answers, because only one of them means the customer should try again.
 */
export const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = searchParams.get("order_id");
  const urlSaysFailed = searchParams.get("status") === "failed";
  const urlReason = searchParams.get("reason") || "unknown_error";

  const [view, setView] = useState(() =>
    urlSaysFailed
      ? {
          phase: "failed",
          title: "Payment failed",
          body: messageFor(urlReason),
          canRetry: true,
        }
      : VERIFYING
  );
  const [payment, setPayment] = useState(null);

  // payment_result_viewed fires once per visit, on the first settled answer,
  // never per poll.
  const resultTrackedRef = useRef(false);
  const trackResult = (status, extra = {}) => {
    if (resultTrackedRef.current) return;
    resultTrackedRef.current = true;
    track(EVENTS.PAYMENT_RESULT_VIEWED, { order_id: orderId || null, status, ...extra });
  };
  const trackResultRef = useRef(trackResult);
  trackResultRef.current = trackResult;

  // Returned from the gateway already marked failed: that is the result.
  useEffect(() => {
    if (urlSaysFailed) trackResultRef.current("failed", { reason: urlReason });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The redirect is fired from the poller, so it must not depend on state that
  // has only just been queued.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });

  /* ---- No order id: nothing to verify ---- */

  useEffect(() => {
    if (orderId || urlSaysFailed) return;

    console.error("OrderConfirmation opened without an order_id");
    setView({
      phase: "failed",
      title: "Payment verification failed",
      body: "Missing order information. Redirecting back to checkout...",
      canRetry: false,
    });

    const t = setTimeout(
      () => navigateRef.current("/checkout?status=failed&reason=missing_order_id"),
      MISSING_ORDER_REDIRECT_MS
    );
    return () => clearTimeout(t);
  }, [orderId, urlSaysFailed]);

  /* ---- Verification ---- */

  useEffect(() => {
    if (!orderId || urlSaysFailed) return;

    let cancelled = false;
    let timer = null;
    let pending = 0; // times the gateway said "not yet"
    let errors = 0; // times we could not ask

    const stop = () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };

    const giveUp = (title, reason) => {
      trackResultRef.current(pending > 0 ? "pending" : "failed", { reason });
      setView({ phase: "failed", title, body: "Please try again", canRetry: true });
    };

    const poll = () => {
      if (cancelled) return;

      Api.ProcessPaymentV4({ order_id: orderId })
        .then(({ data: result }) => {
          if (cancelled || !result) return;
          errors = 0;
          setPayment(result);

          // Paid is not the same as delivered. Both must hold before anyone is
          // told their order went through.
          if (result.status === "paid" && result.is_purchase_done) {
            stop();
            trackResultRef.current("success", {
              amount: Number(result.amount) || null,
              currency: String(result.currency || "INR").toUpperCase(),
              gateway: result.gateway || null,
              method: result.payment_method || null,
            });
            setView({
              phase: "success",
              title: "Payment successful!",
              body: "Redirecting you to your order...",
            });
            timer = setTimeout(
              () => navigateRef.current(`/order?order_id=${result.order_id}`),
              REDIRECT_HOLD_MS
            );
            return;
          }

          if (result.status === "failed") {
            stop();
            trackResultRef.current("failed", {
              gateway: result.gateway || null,
              reason: result.error || "unknown",
            });
            setView({
              phase: "failed",
              title: "Payment failed",
              body: result.error
                ? `Error: ${result.error}`
                : messageFor("payment_not_successful", result.gateway),
              canRetry: true,
            });
            return;
          }

          if (result.status === "pending") {
            pending += 1;
            // A pending payment is still live, so we keep waiting - but say so,
            // and offer a way out rather than spinning silently.
            if (pending >= SLOW_AFTER) {
              setView({
                phase: "slow",
                title: "Payment is taking longer than expected",
                body: "You can wait or try again",
                canRetry: true,
              });
            }
            timer = setTimeout(poll, pending >= SLOW_AFTER ? PATIENT_POLL_MS : POLL_MS);
            return;
          }

          // A status we do not recognise. Give it a few more looks before
          // declaring the verification itself broken.
          pending += 1;
          if (pending >= GIVE_UP_AFTER) {
            stop();
            giveUp("Unable to verify payment status", "unrecognised_status");
            return;
          }
          timer = setTimeout(poll, POLL_MS);
        })
        .catch((err) => {
          if (cancelled) return;
          console.warn("Payment verification request failed:", err?.message || err);
          errors += 1;
          if (errors >= GIVE_UP_AFTER) {
            stop();
            giveUp("Payment verification failed", "verification_error");
            return;
          }
          timer = setTimeout(
            poll,
            Math.min(POLL_MS * 1.5 ** errors, ERROR_BACKOFF_CAP_MS)
          );
        });
    };

    timer = setTimeout(poll, FIRST_POLL_MS);
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, urlSaysFailed]);

  /* ---- Actions ---- */

  const retry = useCallback(() => {
    navigate(orderId ? `/checkout?retry=true&order_id=${orderId}` : "/checkout");
  }, [navigate, orderId]);

  const cancel = useCallback(() => {
    track(EVENTS.PAYMENT_ABANDONED, { order_id: orderId || null, stage: "confirmation" });
    // Best effort: the customer leaves either way.
    const leave = () => navigate("/marketplace");
    if (payment?.payment_id) {
      Api.CancelPayment({ payment_id: payment.payment_id }).then(leave, leave);
    } else {
      leave();
    }
  }, [navigate, payment, orderId]);

  /* ---- Render ---- */

  const isError = view.phase === "failed";
  const isSuccess = view.phase === "success";
  const showActions = view.canRetry || view.phase === "slow";

  const reference =
    isError && payment
      ? [
          ["Payment ID", payment.payment_id],
          ["Order ID", payment.order_id],
          ["Gateway", payment.gateway],
          ["Error", payment.error],
        ].filter(([, value]) => value)
      : [];

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        {isSuccess ? (
          <span className={`${styles.icon} ${styles.iconSuccess}`} aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
        ) : isError ? (
          <span className={`${styles.icon} ${styles.iconError}`} aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </span>
        ) : (
          <span className={`${styles.icon} ${styles.iconWaiting}`} aria-hidden="true">
            <span className={styles.spinner} />
          </span>
        )}

        <p
          className={`${styles.title} ${isError ? styles.titleError : ""}`}
          role="status"
          aria-live="polite"
        >
          {view.title}
        </p>
        <p className={styles.body}>{view.body}</p>

        {reference.length > 0 && (
          <div className={styles.details}>
            {reference.map(([label, value]) => (
              <div className={styles.detailRow} key={label}>
                <span className={styles.detailLabel}>{label}</span>
                <span className={styles.detailValue}>{value}</span>
              </div>
            ))}
          </div>
        )}

        {showActions && (
          <>
            <div className={styles.actions}>
              <button type="button" className={styles.primary} onClick={retry}>
                Try payment again
              </button>
              {/* Cancelling only makes sense while the payment is still live. */}
              {view.phase === "slow" && (
                <button type="button" className={styles.secondary} onClick={cancel}>
                  Cancel payment
                </button>
              )}
            </div>

            {view.phase === "slow" && (
              <p className={styles.note}>
                Still verifying your payment in the background...
              </p>
            )}
          </>
        )}

        {isError && !view.canRetry && (
          <p className={styles.note}>
            If you've been charged, don't worry. Our team will resolve this issue.
          </p>
        )}

        {isError && (
          <p className={styles.help}>
            Need help?
            <a
              className={styles.helpLink}
              href={`https://wa.me/${SUPPORT_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              WhatsApp Support
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default OrderConfirmation;
